import React, { useState, useEffect } from 'react'
import { X, Printer, Eye, Loader2 } from 'lucide-react'
import { generateLabelPDF, mapEntityToTemplateData, printPDF } from '../../lib/printService'

interface PrintDialogProps {
  isOpen: boolean
  onClose: () => void
  entityType: 'item' | 'location' | 'shelf' | 'tote'
  entityData: any
  categoryId?: string
}

interface Template {
  id: number
  name: string
  description: string
  width: number
  height: number
  orientation: string
  scenario: string
  zones: any[]
  is_default?: boolean
  category_id?: string
}

export default function PrintDialog({
  isOpen,
  onClose,
  entityType,
  entityData,
  categoryId
}: PrintDialogProps) {
  const [templates, setTemplates] = useState<Template[]>([])
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null)
  const [quantity, setQuantity] = useState(1)
  const [loading, setLoading] = useState(false)
  const [printing, setPrinting] = useState(false)
  const [previewing, setPreviewing] = useState(false)

  // Fetch templates for this scenario
  useEffect(() => {
    if (isOpen) {
      fetchTemplates()
    }
  }, [isOpen, entityType])

  const fetchTemplates = async () => {
    setLoading(true)
    try {
      const response = await fetch(`/api/templates-pg?scenario=${entityType}`)
      const result = await response.json()

      if (result.success) {
        const scenarioTemplates = result.data.filter((t: Template) =>
          t.scenario === entityType && t.zones && t.zones.length > 0
        )
        setTemplates(scenarioTemplates)

        // Auto-select template
        if (scenarioTemplates.length > 0) {
          // Try to find category-specific template if item
          if (entityType === 'item' && categoryId) {
            const categoryTemplate = scenarioTemplates.find((t: any) =>
              t.category_id === categoryId
            )
            if (categoryTemplate) {
              setSelectedTemplateId(categoryTemplate.id)
              return
            }
          }

          // Fallback to default template
          const defaultTemplate = scenarioTemplates.find((t: any) => t.is_default)
          if (defaultTemplate) {
            setSelectedTemplateId(defaultTemplate.id)
          } else {
            // Just use first template
            setSelectedTemplateId(scenarioTemplates[0].id)
          }
        }
      }
    } catch (error) {
      console.error('Error fetching templates:', error)
    } finally {
      setLoading(false)
    }
  }

  const handlePrint = async () => {
    if (!selectedTemplateId) {
      alert('Please select a template')
      return
    }

    setPrinting(true)
    try {
      const template = templates.find(t => t.id === selectedTemplateId)
      if (!template) {
        throw new Error('Template not found')
      }

      // Map entity data to template format
      const mappedData = mapEntityToTemplateData(entityType, entityData)

      // Generate PDF
      const pdf = await generateLabelPDF(template, mappedData, quantity)

      // Print
      printPDF(pdf)

      // Close dialog after short delay
      setTimeout(() => {
        onClose()
      }, 500)
    } catch (error) {
      console.error('Print error:', error)
      alert('Failed to print label. Please try again.')
    } finally {
      setPrinting(false)
    }
  }

  const handlePreview = async () => {
    if (!selectedTemplateId) {
      alert('Please select a template')
      return
    }

    setPreviewing(true)
    try {
      const template = templates.find(t => t.id === selectedTemplateId)
      if (!template) return

      const mappedData = mapEntityToTemplateData(entityType, entityData)
      const pdf = await generateLabelPDF(template, mappedData, 1)

      // Open PDF in new tab for preview
      const blob = pdf.output('blob')
      const url = URL.createObjectURL(blob)
      window.open(url, '_blank')
    } catch (error) {
      console.error('Preview error:', error)
    } finally {
      setPreviewing(false)
    }
  }

  const selectedTemplate = templates.find(t => t.id === selectedTemplateId)

  if (!isOpen) return null

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      zIndex: 9999,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden'
    }}>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          backdropFilter: 'blur(4px)'
        }}
      />

      {/* Modal */}
      <div style={{
        position: 'relative',
        width: '90%',
        maxWidth: '600px',
        background: 'linear-gradient(to bottom, #0a0e1a, #1a1f2e)',
        border: '2px solid rgba(0, 240, 255, 0.5)',
        borderRadius: '12px',
        boxShadow: '0 0 40px rgba(0, 240, 255, 0.3), inset 0 0 80px rgba(0, 240, 255, 0.05)',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '24px',
          borderBottom: '1px solid rgba(0, 240, 255, 0.2)',
          background: 'linear-gradient(to right, rgba(0, 240, 255, 0.1), transparent)',
          position: 'relative',
          borderRadius: '12px 12px 0 0'
        }}>
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '2px',
            background: 'linear-gradient(to right, transparent, rgba(0, 240, 255, 0.8), transparent)'
          }} />

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              padding: '10px',
              borderRadius: '8px',
              background: 'rgba(0, 240, 255, 0.2)',
              border: '1px solid rgba(0, 240, 255, 0.4)',
              boxShadow: '0 0 20px rgba(0, 240, 255, 0.3)'
            }}>
              <Printer style={{ width: '24px', height: '24px', color: '#00F0FF' }} />
            </div>
            <h2 style={{
              fontSize: '1.5rem',
              fontWeight: 700,
              color: '#E0E6F0',
              fontFamily: 'Rajdhani, sans-serif',
              textTransform: 'uppercase',
              letterSpacing: '1.5px',
              textShadow: '0 0 20px rgba(0, 240, 255, 0.5)',
              margin: 0
            }}>
              Print Label
            </h2>
          </div>

          <button
            onClick={onClose}
            style={{
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: '6px',
              padding: '8px',
              cursor: 'pointer',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(239, 68, 68, 0.2)'
              e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.5)'
              e.currentTarget.style.boxShadow = '0 0 15px rgba(239, 68, 68, 0.4)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)'
              e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.3)'
              e.currentTarget.style.boxShadow = 'none'
            }}
          >
            <X style={{ width: '20px', height: '20px', color: '#EF4444' }} />
          </button>
        </div>

        {/* Content */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '24px'
        }}>
          {loading ? (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '40px',
              gap: '12px'
            }}>
              <Loader2 style={{ width: '24px', height: '24px', color: '#00F0FF', animation: 'spin 1s linear infinite' }} />
              <span style={{ color: '#E0E6F0', fontSize: '0.875rem' }}>Loading templates...</span>
            </div>
          ) : templates.length === 0 ? (
            <div style={{
              padding: '40px',
              textAlign: 'center',
              color: '#9CA3AF'
            }}>
              <p style={{ margin: 0 }}>No templates found for {entityType}.</p>
              <p style={{ margin: '8px 0 0 0', fontSize: '0.875rem' }}>Create a template in the Label Designer first.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {/* Entity Info */}
              <div style={{
                background: 'linear-gradient(135deg, rgba(0, 240, 255, 0.05), rgba(59, 130, 246, 0.05))',
                border: '1px solid rgba(0, 240, 255, 0.3)',
                borderRadius: '8px',
                padding: '16px'
              }}>
                <div style={{ fontSize: '0.75rem', color: '#9CA3AF', textTransform: 'uppercase', marginBottom: '8px' }}>
                  {entityType.toUpperCase()}
                </div>
                <div style={{ fontSize: '1.125rem', color: '#E0E6F0', fontWeight: 600 }}>
                  {entityData?.name || entityData?.title || 'Unknown'}
                </div>
                {entityData?.ic && (
                  <div style={{ fontSize: '0.875rem', color: '#00F0FF', fontFamily: 'monospace', marginTop: '4px' }}>
                    IC: {entityData.ic}
                  </div>
                )}
              </div>

              {/* Template Selection */}
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  color: '#E0E6F0',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: '8px'
                }}>
                  Template
                </label>
                <select
                  value={selectedTemplateId || ''}
                  onChange={(e) => setSelectedTemplateId(Number(e.target.value))}
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    background: 'rgba(17, 24, 39, 0.8)',
                    border: '2px solid rgba(0, 240, 255, 0.3)',
                    borderRadius: '6px',
                    color: '#E0E6F0',
                    fontSize: '0.875rem',
                    outline: 'none',
                    transition: 'all 0.2s',
                    cursor: 'pointer'
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(0, 240, 255, 0.6)'
                    e.currentTarget.style.boxShadow = '0 0 15px rgba(0, 240, 255, 0.3)'
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(0, 240, 255, 0.3)'
                    e.currentTarget.style.boxShadow = 'none'
                  }}
                >
                  {templates.map(template => (
                    <option key={template.id} value={template.id}>
                      {template.name} ({template.width}" x {template.height}")
                    </option>
                  ))}
                </select>
                {selectedTemplate && (
                  <div style={{ marginTop: '8px', fontSize: '0.75rem', color: '#9CA3AF' }}>
                    {selectedTemplate.description}
                  </div>
                )}
              </div>

              {/* Quantity */}
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  color: '#E0E6F0',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: '8px'
                }}>
                  Quantity (Optional)
                </label>
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={quantity}
                  onChange={(e) => setQuantity(Math.max(1, Math.min(100, Number(e.target.value))))}
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    background: 'rgba(17, 24, 39, 0.8)',
                    border: '2px solid rgba(0, 240, 255, 0.3)',
                    borderRadius: '6px',
                    color: '#E0E6F0',
                    fontSize: '0.875rem',
                    outline: 'none',
                    transition: 'all 0.2s'
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(0, 240, 255, 0.6)'
                    e.currentTarget.style.boxShadow = '0 0 15px rgba(0, 240, 255, 0.3)'
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(0, 240, 255, 0.3)'
                    e.currentTarget.style.boxShadow = 'none'
                  }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {templates.length > 0 && (
          <div style={{
            padding: '24px',
            borderTop: '1px solid rgba(0, 240, 255, 0.2)',
            display: 'flex',
            gap: '12px',
            background: 'rgba(0, 0, 0, 0.2)'
          }}>
            <button
              onClick={handlePreview}
              disabled={!selectedTemplateId || previewing}
              style={{
                flex: 1,
                padding: '12px 20px',
                background: 'rgba(107, 114, 128, 0.2)',
                border: '2px solid rgba(107, 114, 128, 0.4)',
                borderRadius: '8px',
                color: '#9CA3AF',
                fontSize: '0.875rem',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '1px',
                cursor: !selectedTemplateId || previewing ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s',
                fontFamily: 'Rajdhani, sans-serif',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                opacity: !selectedTemplateId || previewing ? 0.5 : 1
              }}
              onMouseEnter={(e) => {
                if (selectedTemplateId && !previewing) {
                  e.currentTarget.style.background = 'rgba(107, 114, 128, 0.3)'
                  e.currentTarget.style.borderColor = 'rgba(107, 114, 128, 0.6)'
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(107, 114, 128, 0.2)'
                e.currentTarget.style.borderColor = 'rgba(107, 114, 128, 0.4)'
              }}
            >
              {previewing ? (
                <Loader2 style={{ width: '16px', height: '16px', animation: 'spin 1s linear infinite' }} />
              ) : (
                <Eye style={{ width: '16px', height: '16px' }} />
              )}
              Preview
            </button>

            <button
              onClick={handlePrint}
              disabled={!selectedTemplateId || printing}
              style={{
                flex: 1,
                padding: '12px 20px',
                background: printing || !selectedTemplateId
                  ? 'rgba(0, 240, 255, 0.3)'
                  : 'linear-gradient(135deg, #00F0FF, #00B8D4)',
                border: '2px solid rgba(0, 240, 255, 0.6)',
                borderRadius: '8px',
                color: '#FFFFFF',
                fontSize: '0.875rem',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '1px',
                cursor: !selectedTemplateId || printing ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                fontFamily: 'Rajdhani, sans-serif',
                boxShadow: printing || !selectedTemplateId ? 'none' : '0 0 20px rgba(0, 240, 255, 0.4)',
                opacity: printing || !selectedTemplateId ? 0.6 : 1
              }}
              onMouseEnter={(e) => {
                if (selectedTemplateId && !printing) {
                  e.currentTarget.style.boxShadow = '0 0 30px rgba(0, 240, 255, 0.6)'
                  e.currentTarget.style.transform = 'scale(1.02)'
                }
              }}
              onMouseLeave={(e) => {
                if (selectedTemplateId && !printing) {
                  e.currentTarget.style.boxShadow = '0 0 20px rgba(0, 240, 255, 0.4)'
                  e.currentTarget.style.transform = 'scale(1)'
                }
              }}
            >
              {printing ? (
                <>
                  <Loader2 style={{ width: '16px', height: '16px', animation: 'spin 1s linear infinite' }} />
                  Printing...
                </>
              ) : (
                <>
                  <Printer style={{ width: '16px', height: '16px' }} />
                  Print Label
                </>
              )}
            </button>
          </div>
        )}
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  )
}
